import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * Серая плашка r10 внутри белой карточки — единственный разрешённый способ
 * «вложить» поверхность, раз карточки не вкладываются. Радиус на шаг туже
 * контейнера (10 против 12), как и любая медиа внутри карточки.
 *
 * Паддинги взяты из макетов и не округлены: показатель дышит 18/20, строка с
 * иконкой — 14/16, плитка сетки — ровные 16, компактная плашка меню — 12/14.
 */
const sunkenPanelVariants = cva("flex rounded-control", {
	variants: {
		padding: {
			stat: "px-5 py-[18px]",
			row: "px-4 py-3.5",
			tile: "p-4",
			compact: "px-3.5 py-3",
			plan: "p-5",
			none: "",
		},
		tone: {
			default: "bg-sunken",
			cool: "bg-chip-cool",
			/** Зелёная плашка достижения — бренд как акцент идентичности, не как декор. */
			brand: "bg-chip-brand",
			/**
			 * Белая плитка с волосяной обводкой внутрь — «место, которое можно
			 * занять»: сервис ещё не подключён, слот пуст.
			 *
			 * Единственная обводка на карточках витрины, и она сознательное
			 * исключение из «no border» брифа. Заливкой это не решается: плитка
			 * обязана читаться белой (то есть пустой), а белое на белом не
			 * отличается от фона карточки, и слот выглядел бы как подключённый
			 * сервис без содержимого.
			 *
			 * Обводка `inset`-тенью, а не `border`: рамка съела бы 1px внутрь
			 * геометрии и сдвинула бы содержимое относительно соседних плиток на
			 * той же сетке.
			 *
			 * Вариант заведён, чтобы это исключение было одно и в одном месте:
			 * до него тот же `shadow-[inset_0_0_0_1px_…]` стоял inline в витрине
			 * (аудит В6), и вторая такая плитка неизбежно разошлась бы с первой.
			 */
			outlined: "bg-card shadow-[inset_0_0_0_1px_var(--color-hairline)]",
		},
		direction: {
			row: "flex-row items-center",
			column: "flex-col",
		},
	},
	defaultVariants: { padding: "row", tone: "default", direction: "row" },
});

export interface SunkenPanelProps
	extends HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof sunkenPanelVariants> {
	children?: ReactNode;
}

export function SunkenPanel({
	padding,
	tone,
	direction,
	className,
	children,
	...rest
}: SunkenPanelProps) {
	return (
		<div className={cn(sunkenPanelVariants({ padding, tone, direction }), className)} {...rest}>
			{children}
		</div>
	);
}

export { sunkenPanelVariants };
